import type { BoundingSphere, ObjectId, View } from "@novorender/api";
import { glMatrix, vec3 } from "gl-matrix";
import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import type { AppDispatch } from "app/store";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { getCameraDir } from "features/engine2D/utils";
import { CameraType, renderActions, selectCameraType } from "features/render";
import { objIdsToTotalBoundingSphere } from "utils/objectData";

export default function useFlyTo() {
    const {
        state: { view, db, scene },
    } = useExplorerGlobals(false);
    const cameraType = useAppSelector(selectCameraType);
    const dispatch = useAppDispatch();

    return useCallback(
        async (params: { sphere: BoundingSphere } | { objectIds: ObjectId[]; abortSignal: AbortSignal }) => {
            if (!view || !db || !scene) {
                return;
            }

            let sphere: BoundingSphere | undefined;
            if ("sphere" in params) {
                sphere = params.sphere;
            } else {
                sphere = await objIdsToTotalBoundingSphere({
                    ids: params.objectIds,
                    abortSignal: params.abortSignal,
                    db,
                    view,
                    flip: !vec3.equals(scene.up ?? [0, 1, 0], [0, 0, 1]),
                }).catch((error) => {
                    console.error("Failed to calculate bounding sphere:", error);
                    return undefined;
                });
                if (params.abortSignal.aborted) {
                    return;
                }
            }

            if (sphere) {
                flyToSphere({ sphere, cameraType, view, dispatch });
            }
        },
        [dispatch, view, cameraType, db, scene],
    );
}

function flyToSphere({
    sphere,
    cameraType,
    view,
    dispatch,
}: {
    sphere: BoundingSphere;
    cameraType: CameraType;
    view: View;
    dispatch: AppDispatch;
}) {
    if (cameraType === CameraType.Pinhole) {
        dispatch(
            renderActions.setCamera({
                type: CameraType.Pinhole,
                zoomTo: sphere,
            }),
        );
    } else {
        const cameraDir = getCameraDir(view.renderState.camera.rotation);
        const pinholeFov = view.controllers.flight.fov;
        const dist = Math.max(sphere.radius / Math.tan(glMatrix.toRadian(pinholeFov) / 2), sphere.radius);
        const offset = -Math.min(dist, view.renderState.camera.far * 0.9);
        const position = vec3.scaleAndAdd(vec3.create(), sphere.center, cameraDir, offset);

        dispatch(
            renderActions.setCamera({
                type: CameraType.Orthographic,
                goTo: {
                    position,
                    rotation: view.renderState.camera.rotation,
                    fov: sphere.radius * 2,
                },
            }),
        );
    }
}
